import React, { useEffect, useState } from "react";
import getApiBaseUrl from "../utils/api"; // adjust this relative path to match where api.js actually lives in your src/ folder

// ─── CONSTANTS (mirrored from ProfilePage) ────────────────────────────────────

const FACTORS = {
  A: "Warmth", B: "Reasoning", C: "Emotional Stability", E: "Dominance",
  F: "Liveliness", G: "Rule-Consciousness", H: "Social Boldness", I: "Sensitivity",
  L: "Vigilance", M: "Abstractedness", N: "Privateness", O: "Apprehension",
  Q1: "Openness to Change", Q2: "Self-Reliance", Q3: "Perfectionism", Q4: "Tension",
};

const CAREER_ANCHORS_MAP = {
  AU: "Autonomy/Independence", SE: "Security/Stability", TF: "Technical/Functional",
  GM: "General Management", EC: "Entrepreneurial Creativity", SV: "Service/Dedication",
  CH: "Pure Challenge", LS: "Lifestyle",
};

const WORD_MEANINGS = {
  happy: "You have a positive mindset and naturally focus on the brighter side of situations.",
  lazy: "You prefer comfort and may avoid unnecessary effort unless required.",
  impatient: "You like quick results and may feel frustrated with delays.",
  patient: "You stay calm and composed even when things take time.",
  genuine: "You are real, honest, and value authenticity in yourself and others.",
  elegant: "You appreciate grace, style, and refined behavior.",
  peaceful: "You prefer calm environments and avoid unnecessary conflict.",
  reserved: "You are quiet and take time before opening up to others.",
  witty: "You are quick-thinking and enjoy humor and clever conversations.",
  sentimental: "You value emotions, memories, and deep connections.",
  lovely: "You see kindness and warmth as important traits.",
  dependent: "You may rely on others for support or decisions.",
  loyal: "You value commitment and stand by people you care about.",
  lethargic: "You may feel low energy or prefer slower-paced environments.",
  dramatic: "You express emotions strongly and intensely.",
  overdramatic: "You may sometimes react more emotionally than needed.",
  talented: "You recognize skills and abilities, both in yourself and others.",
  charismatic: "You are naturally attractive in personality and influence people easily.",
  honest: "You value truth and transparency in communication.",
  naive: "You tend to trust easily and may see things in a simple way.",
  shy: "You may feel hesitant in social situations, especially new ones.",
  kind: "You are caring, compassionate, and considerate toward others.",
  passionate: "You feel strongly about things and put energy into what you love.",
  insecure: "You may sometimes doubt yourself or your abilities.",
  secure: "You are confident and comfortable with yourself.",
  thoughtful: "You think deeply and consider others before acting.",
  restless: "You like movement, change, and may feel bored easily.",
  outgoing: "You enjoy socializing and interacting with people.",
  sweet: "You are gentle, friendly, and easy to be around.",
  sincere: "You are honest and true in your actions and intentions.",
  charming: "You naturally attract people with your personality.",
  courageous: "You are brave and willing to face challenges.",
  compassionate: "You deeply care about others' feelings.",
  helpful: "You like supporting and assisting others.",
  stubborn: "You stick strongly to your opinions and decisions.",
  calculating: "You think logically and plan before acting.",
};

// ─── SHARED UI HELPERS ────────────────────────────────────────────────────────

const A = {
  tip: (text) => (
    <div style={{
      marginTop: "14px",
      background: "rgba(25,253,145,0.08)",
      border: "1px solid rgba(25,253,145,0.3)",
      borderRadius: "10px",
      padding: "10px 14px",
    }}>
      <div style={{ fontSize: "0.9rem", fontWeight: 600, color: "#19fd91", fontStyle: "italic" }}>
        💡 Growth Tip
      </div>
      <div style={{ marginTop: "6px", fontSize: "0.88rem", color: "#19fd91", fontStyle: "italic", lineHeight: 1.5 }}>
        {text}
      </div>
    </div>
  ),

  sectionHead: (text) => (
    <div style={{ fontWeight: 700, marginBottom: "8px", marginTop: "14px", color: "#e0e0e0" }}>
      {text}
    </div>
  ),

  para: (text, extra = {}) => (
    <p style={{ fontSize: "0.88rem", color: "#aaa", lineHeight: 1.6, whiteSpace: "pre-line", margin: "6px 0", ...extra }}>
      {text}
    </p>
  ),

  hero: (score, label, sub) => (
    <div style={{
      background: "rgba(25,253,145,0.08)",
      border: "1px solid rgba(25,253,145,0.25)",
      borderRadius: "12px",
      padding: "1.2rem",
      textAlign: "center",
      marginBottom: "14px",
    }}>
      <div style={{ fontSize: "2.5rem", fontWeight: 800, color: "#19fd91", lineHeight: 1 }}>{score}</div>
      {label && <div style={{ fontSize: "1rem", fontWeight: 600, color: "#e0e0e0", marginTop: "6px" }}>{label}</div>}
      {sub && <div style={{ fontSize: "0.85rem", color: "#aaa", marginTop: "4px" }}>{sub}</div>}
    </div>
  ),

  kvGrid: (pairs) => (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "8px" }}>
      {pairs.map(([k, v], i) => (
        <div key={i} style={{
          background: "#1e1e1e", border: "1px solid #2a2a2a",
          borderRadius: "8px", padding: "10px 12px",
        }}>
          <div style={{ fontSize: "11px", color: "#555", marginBottom: "3px", textTransform: "uppercase", letterSpacing: "0.05em" }}>{k}</div>
          <div style={{ fontSize: "13px", fontWeight: 500, color: "#e0e0e0", wordBreak: "break-word" }}>{String(v)}</div>
        </div>
      ))}
    </div>
  ),

  infoBox: (children, extra = {}) => (
    <div style={{
      background: "rgba(255,255,255,0.03)",
      border: "1px solid rgba(255,255,255,0.06)",
      borderRadius: "10px",
      padding: "12px",
      lineHeight: 1.6,
      marginTop: "12px",
      ...extra,
    }}>
      {children}
    </div>
  ),

  divider: () => <div style={{ height: "1px", background: "#1e1e1e", margin: "14px 0" }} />,
};

// ─── FULL RESULT RENDERER ─────────────────────────────────────────────────────

function renderDetailedResult(item) {
  if (!item) return <p style={{ color: "#aaa" }}>No details available.</p>;

  const testName = item.testName || "";

  const META_KEYS = new Set(["testName", "date", "_id", "user", "__v"]);
  let result = item.result;
  if (!result || typeof result !== "object" || Object.keys(result).length === 0) {
    result = Object.fromEntries(Object.entries(item).filter(([k]) => !META_KEYS.has(k)));
  }

  // ── 1. MBTI ─────────────────────────────────────────────────────────
  if (result?.type && /^[EI][NS][TF][JP]$/.test(result.type)) {
    const info = result.description || {};
    const letterMeaning = {
      E: "Gains energy from people and social interaction.",
      I: "Recharges by spending time alone.",
      S: "Focuses on real facts and practical details.",
      N: "Focuses on ideas and future possibilities.",
      T: "Makes decisions using logic.",
      F: "Makes decisions using feelings and values.",
      J: "Prefers planning and structure.",
      P: "Prefers flexibility and freedom.",
    };

    return (
      <div>
        {A.hero(result.type, info.label || "", info.text || "")}
        {A.sectionHead("Personality Breakdown")}
        {result.type.split("").map((l, i) => (
          <div key={i} style={{ fontSize: "0.88rem", color: "#aaa", marginBottom: "5px", lineHeight: 1.5 }}>
            <b style={{ color: "#19fd91" }}>{l}</b> — {letterMeaning[l]}
          </div>
        ))}
        {A.infoBox(<>
          {A.sectionHead("What this means in real life")}
          {A.para("Your personality affects how you think, act, and respond daily.\nIt influences communication, stress handling, and decision-making.\n\nYou may prefer either structure or flexibility depending on your type.\nUnderstanding this helps you choose better career paths, improve relationships, and manage stress more effectively.")}
        </>, { background: "rgba(25,253,145,0.06)" })}
        {A.infoBox(<>
          {A.sectionHead("Strengths & Blind Spots")}
          {A.para("• You naturally build strengths based on your personality.\n• You may also have areas that feel difficult or uncomfortable.\n• These are not weaknesses, just areas for awareness.\n• Awareness helps you grow and become balanced.")}
        </>)}
        {A.tip("You feel best in environments that match your personality. The right fit reduces stress and increases motivation.")}
      </div>
    );
  }

  // ── 2. LEFT / RIGHT BRAIN ───────────────────────────────────────────
  if (testName.toLowerCase().includes("left brain") && result?.dominance) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        {A.hero(
          result.isRightBrain ? "Right Brain" : "Left Brain",
          result.dominance,
          result.isRightBrain
            ? "You tend to think holistically, creatively, and intuitively."
            : "You tend to think analytically, logically, and step-by-step."
        )}
        {result.careers?.length > 0 && (
          <div style={{
            background: "rgba(25,253,145,0.05)", border: "1px solid rgba(25,253,145,0.2)",
            borderRadius: "12px", padding: "1rem",
          }}>
            <div style={{ fontWeight: 700, color: "#19fd91", marginBottom: "10px" }}>🎯 Ideal Career Paths</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              {result.careers.map((c, i) => (
                <div key={i} style={{ background: "#1e1e1e", padding: "8px", borderRadius: "8px", fontSize: "0.85rem" }}>✓ {c}</div>
              ))}
            </div>
          </div>
        )}
        {A.infoBox(<>
          <div style={{ fontWeight: 700, marginBottom: "6px" }}>🧠 How your brain works</div>
          {A.para(result.isRightBrain
            ? "You process information visually and emotionally. You focus on patterns, creativity, and big-picture thinking.\n\nYou are naturally intuitive and imaginative, often thinking in ideas and possibilities."
            : "You process information logically and step-by-step. You focus on structure, facts, and clear reasoning.\n\nYou are naturally analytical and practical, often thinking in structured steps and solutions."
          )}
        </>, { background: "rgba(25,253,145,0.06)" })}
        {A.infoBox(<>
          <div style={{ fontWeight: 700, marginBottom: "6px" }}>⚖️ Strengths & Challenges</div>
          {A.para(result.isRightBrain
            ? "• Strong creativity and imagination\n• Good at understanding emotions and people\n• May struggle with structure and planning\n• Can get distracted easily"
            : "• Strong logical and analytical thinking\n• Good at planning and organization\n• May struggle with creativity or flexibility\n• Can become too rigid or detail-focused"
          )}
        </>)}
        {A.tip(result.isRightBrain
          ? "Try building routines and structure in your daily life to balance your creativity."
          : "Try exploring creative activities and flexible thinking to balance your logical approach."
        )}
      </div>
    );
  }

  // ── 3. BIG FIVE ─────────────────────────────────────────────────────
  if (testName.toLowerCase().includes("big five") && result?.raw) {
    const r = result.raw;
    const getLevel = (v) => v >= 70 ? "High" : v >= 40 ? "Moderate" : "Low";
    const traits = [
      {
        key: "E", name: "Extraversion",
        high: "You are very outgoing and energetic. You enjoy being around people and feel more alive in group environments.",
        mid: "You are balanced. You enjoy time with others but also value your personal space.",
        low: "You are more quiet and reserved. You prefer calm environments and meaningful conversations.",
      },
      {
        key: "A", name: "Agreeableness",
        high: "You are kind, caring, and supportive. You try to help others and avoid conflict.",
        mid: "You are balanced. You can be kind and understanding, but also know when to be practical.",
        low: "You are more direct and practical. You focus on facts and honesty.",
      },
      {
        key: "C", name: "Conscientiousness",
        high: "You are highly organized and disciplined. You plan things well and complete work responsibly.",
        mid: "You are somewhat organized. You can manage tasks, but may sometimes be flexible or delay things.",
        low: "You prefer flexibility over strict planning. You like doing things your own way.",
      },
      {
        key: "N", name: "Emotional Stability",
        high: "You may feel stress or emotions strongly. Small problems may sometimes feel overwhelming.",
        mid: "You are somewhat stable emotionally. You feel stress sometimes but can handle it in many situations.",
        low: "You are calm and emotionally stable. You handle pressure well and stay relaxed.",
      },
      {
        key: "O", name: "Openness",
        high: "You are creative and curious. You enjoy learning new things and exploring new ideas.",
        mid: "You are open to new experiences but also enjoy familiar routines.",
        low: "You prefer stability and routine. You feel comfortable with familiar methods.",
      },
    ];

    return (
      <div>
        <div style={{ fontSize: "0.88rem", color: "#aaa", marginBottom: "12px" }}>Big Five Personality Report</div>
        <div style={{
          background: "rgba(25,253,145,0.08)", border: "1px solid rgba(25,253,145,0.3)",
          borderRadius: "10px", padding: "1rem", textAlign: "center", marginBottom: "14px",
        }}>
          <p style={{ fontSize: "0.85rem", color: "#aaa" }}>
            This report shows how you think, behave, and interact with others. There is no "good" or "bad" personality — this simply helps you understand yourself better.
          </p>
        </div>
        {traits.map(({ key, name, high, mid, low }) => {
          const val = r[key];
          const text = val >= 70 ? high : val >= 40 ? mid : low;
          return (
            <div key={key} style={{ marginTop: "12px" }}>
              <strong style={{ color: "#e0e0e0" }}>{name} ({getLevel(val)}): {val}</strong>
              <p style={{ fontSize: "0.85rem", color: "#aaa", marginTop: "5px", lineHeight: 1.5 }}>{text}</p>
            </div>
          );
        })}
        <div style={{ marginTop: "14px" }}>
          <strong style={{ color: "#e0e0e0" }}>📌 Final Insight:</strong>
          {A.para("Your personality is unique. Some areas are strong, and some can improve. The goal is not to change who you are — but to understand yourself better and grow step by step.")}
        </div>
        {A.tip("Focus on one small improvement at a time instead of trying to change everything at once.")}
      </div>
    );
  }

  // ── 4. 16PF ─────────────────────────────────────────────────────────
  if (testName.includes("16PF") && result?.averages) {
    return (
      <div>
        <div style={{ fontWeight: 600, color: "#19fd91", marginBottom: "8px" }}>16PF Results</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px", fontSize: "0.88rem" }}>
          {Object.entries(result.averages).map(([k, v]) => (
            <div key={k} style={{ background: "#1e1e1e", padding: "8px 10px", borderRadius: "6px" }}>
              <strong style={{ color: "#19fd91" }}>{k} – {FACTORS[k] || k}:</strong>{" "}
              <span style={{ color: "#aaa" }}>{v} ({result.descriptions?.[k]})</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── 5. TOEFL ────────────────────────────────────────────────────────
  if (testName.toLowerCase().includes("toefl") && result) {
    const rawScore = Number(result.rawScore) || 0;
    const total = Number(result.total) || 0;
    const toeflScore = Number(result.toeflScore) || 0;
    const pct = result.percentage !== undefined
      ? `${result.percentage}%`
      : total ? `${Math.round((rawScore / total) * 100)}%` : "N/A";

    return (
      <div>
        <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#19fd91" }}>
          {testName.replace("TOEFL ", "")}: {toeflScore} / 30
        </div>
        <div style={{ fontSize: "0.95rem", marginTop: "6px", color: "#e0e0e0" }}>
          Raw Score: {rawScore} / {total}
          <span style={{ marginLeft: "8px", color: "#aaa" }}>({pct})</span>
        </div>
      </div>
    );
  }

  // ── 6. IELTS ────────────────────────────────────────────────────────
  if (testName.toLowerCase().includes("ielts") && result?.score !== undefined) {
    const total = Number(result.total) || 40;
    const score = Number(result.score) || 0;
    const scaledRaw = Math.round((score / total) * 40);
    const bandMap = [
      [39,9],[37,8.5],[35,8],[32,7.5],[30,7],[27,6.5],[23,6],
      [19,5.5],[15,5],[13,4.5],[10,4],[7,3.5],[5,3],[3,2.5],[1,2],[0,0],
    ];
    let band = 0;
    for (const [minRaw, b] of bandMap) { if (scaledRaw >= minRaw) { band = b; break; } }
    const pct = result.percentage || (total ? `${Math.round((score / total) * 100)}%` : "N/A");

    return (
      <div>
        <div style={{ fontWeight: 700, color: "#19fd91", fontSize: "1.05rem", marginBottom: "6px" }}>
          Score: {score} / {total}
          <span style={{ fontSize: "0.9rem", color: "#aaa", fontWeight: 600, marginLeft: "6px" }}>({pct})</span>
        </div>
        <div style={{ fontSize: "0.95rem", color: "#e0e0e0", marginTop: "6px" }}>
          Estimated IELTS Band: <strong style={{ color: "#19fd91", marginLeft: "6px" }}>{band}</strong>
        </div>
      </div>
    );
  }

  // ── 7. CAREER ANCHORS (legacy AU/SE/TF/... code format) ─────────────
  if (testName.includes("Career") && result?.topAnchors) {
    const anchors = result.topAnchors.slice(0, 3);
    const getAnchorMeaning = (code) => ({
      AU: "You strongly value independence and dislike being controlled. You prefer freedom in how and when you work.",
      SE: "You prioritize job security and stability over risk or uncertainty.",
      TF: "You are driven by expertise and mastery in a specific skill or technical field.",
      GM: "You are leadership-oriented and naturally inclined toward managing people and systems.",
      EC: "You are innovative, risk-taking, and motivated to build something of your own.",
      SV: "You are motivated by helping others and contributing to society.",
      CH: "You thrive in difficult, competitive, and high-pressure challenges.",
      LS: "You value balance between personal life, family, and career above ambition.",
    }[code] || "");

    const primaryCode = anchors[0]?.[0];
    const summaries = {
      AU: "You are an independent thinker who dislikes restrictions and prefers freedom in decision-making.",
      SE: "You are stability-driven and prefer predictable environments where risks are minimal.",
      TF: "You are skill-focused and want deep mastery in a specialized domain.",
      GM: "You are naturally leadership-oriented and think in terms of managing systems and people.",
      EC: "You are entrepreneurial in mindset and enjoy creating new ideas or businesses.",
      SV: "You are purpose-driven and want your work to have a meaningful social impact.",
      CH: "You are challenge-driven and perform best in difficult, competitive situations.",
      LS: "You are balance-driven and prioritize personal life harmony over career pressure.",
    };

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <div style={{ fontWeight: 700, color: "#19fd91", fontSize: "1.1rem" }}>Career Identity Profile Report</div>
        {primaryCode && (
          <div style={{
            background: "rgba(25,253,145,0.06)", border: "1px solid rgba(25,253,145,0.2)",
            borderRadius: "12px", padding: "1rem", fontSize: "0.9rem", color: "#aaa", lineHeight: 1.7,
          }}>
            <div style={{ fontWeight: 600, marginBottom: "6px", color: "#e0e0e0" }}>🧠 Your Career Personality Profile</div>
            {summaries[primaryCode] || ""}
          </div>
        )}
        {[anchors[0], anchors[1], anchors[2]].map((item, idx) => {
          if (!item) return null;
          const [code, score] = item;
          const labels = ["🥇 Primary Anchor", "🥈 Secondary Anchor", "🥉 Tertiary Anchor"];
          return (
            <div key={idx} style={{ background: "#1e1e1e", border: "1px solid #2a2a2a", borderRadius: "12px", padding: "1rem" }}>
              <div style={{ fontWeight: 700, color: "#19fd91", fontSize: "1rem", marginBottom: "6px" }}>
                {labels[idx]}: {CAREER_ANCHORS_MAP[code] || code}
              </div>
              <div style={{ fontSize: "0.88rem", color: "#aaa", lineHeight: 1.6 }}>
                <b style={{ color: "#ccc" }}>Score:</b> {score}<br />
                <b style={{ color: "#ccc" }}>Meaning:</b> {getAnchorMeaning(code)}
              </div>
            </div>
          );
        })}
        <div style={{
          background: "rgba(25,253,145,0.06)", border: "1px solid rgba(25,253,145,0.2)",
          borderRadius: "12px", padding: "1rem", fontSize: "0.88rem", color: "#aaa", lineHeight: 1.7,
        }}>
          <div style={{ fontWeight: 600, marginBottom: "6px", color: "#e0e0e0" }}>🌍 What This Means in Real Life</div>
          Your top career anchors influence:
          <ul style={{ marginTop: "6px", paddingLeft: "18px" }}>
            <li>What kind of job makes you feel satisfied</li>
            <li>How you react to pressure, leadership, and rules</li>
            <li>Whether you prefer freedom, stability, or responsibility</li>
            <li>Why you may like certain careers and dislike others</li>
          </ul>
        </div>
      </div>
    );
  }

  // ── 7b. CAREER INTEREST ASSESSMENT (topThree/scores category format) ─
  // Handles results shaped like:
  // { scores: [{id,title,score}, ...], topThree: [{id,title,score}, ...] }
  // This is a different data shape from the "topAnchors" branch above
  // (categories like "Advanced Manufacturing", "Healthcare & Human
  // Services", etc. instead of AU/SE/TF/... codes), so it needs its own
  // renderer — otherwise it was falling through to the generic JSON dump.
  if (Array.isArray(result?.topThree) || Array.isArray(result?.scores)) {
    const top = (result.topThree && result.topThree.length > 0)
      ? result.topThree
      : [...(result.scores || [])].sort((a, b) => (b.score || 0) - (a.score || 0)).slice(0, 3);

    const allScores = Array.isArray(result.scores) ? result.scores : [];
    const medals = ["🥇 Top Match", "🥈 Second Match", "🥉 Third Match"];

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <div style={{ fontWeight: 700, color: "#19fd91", fontSize: "1.1rem" }}>Career Interest Assessment</div>

        {top.length > 0 ? (
          top.map((t, idx) => (
            <div key={t.id ?? idx} style={{ background: "#1e1e1e", border: "1px solid #2a2a2a", borderRadius: "12px", padding: "1rem" }}>
              <div style={{ fontWeight: 700, color: "#19fd91", fontSize: "1rem", marginBottom: "4px" }}>
                {medals[idx] || `#${idx + 1}`}: {t.title}
              </div>
              <div style={{ fontSize: "0.88rem", color: "#aaa" }}>
                Score: {t.score}
              </div>
            </div>
          ))
        ) : (
          <p style={{ color: "#aaa" }}>No top interest areas recorded.</p>
        )}

        {allScores.length > 0 && (
          A.infoBox(<>
            {A.sectionHead("All Category Scores")}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              {[...allScores]
                .sort((a, b) => (b.score || 0) - (a.score || 0))
                .map((s, i) => (
                  <div key={s.id ?? i} style={{
                    display: "flex", justifyContent: "space-between",
                    background: "#1e1e1e", border: "1px solid #2a2a2a",
                    borderRadius: "8px", padding: "8px 10px", fontSize: "0.85rem",
                  }}>
                    <span style={{ color: "#ccc" }}>{s.title}</span>
                    <span style={{ color: "#19fd91", fontWeight: 700 }}>{s.score}</span>
                  </div>
                ))}
            </div>
          </>)
        )}

        {A.tip("Your top-scoring interest areas point toward career fields worth exploring further — use them as a starting point, not a final answer.")}
      </div>
    );
  }

  // ── 8. RIASEC ───────────────────────────────────────────────────────
  if (testName.includes("RIASEC") && result?.code) {
    const getRiasecGrowthTip = (type) => ({
      R: "Try improving communication skills along with your practical abilities to grow faster in team environments.",
      I: "Balance analysis with action — don't overthink, start applying your ideas.",
      A: "Build discipline and structure to turn your creativity into real outcomes.",
      S: "Set personal boundaries and focus on your own growth along with helping others.",
      E: "Develop patience and listening skills to become a stronger leader.",
      C: "Be open to change and flexibility instead of sticking only to routines.",
    }[type] || "Focus on improving both your strengths and weaker areas for balanced growth.");

    return (
      <div>
        <h3 style={{ color: "#19fd91", marginBottom: "10px" }}>🎯 RIASEC Career Result</h3>
        <div style={{ padding: "12px", background: "rgba(25,253,145,0.08)", borderRadius: "8px", marginBottom: "12px" }}>
          <p style={{ fontSize: "0.88rem", color: "#aaa" }}>Your interests are based on Holland Code. This helps you choose careers and fields that match your personality.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "8px", marginBottom: "12px" }}>
          {Object.keys(result.counts || {}).map((k) => (
            <div key={k} style={{ padding: "10px", borderRadius: "8px", textAlign: "center", background: "rgba(25,253,145,0.08)" }}>
              <div style={{ fontSize: "0.8rem", color: "#aaa" }}>{k}</div>
              <div style={{ fontSize: "1.3rem", color: "#19fd91" }}>{result.counts[k]}</div>
            </div>
          ))}
        </div>
        <div style={{ padding: "12px", textAlign: "center", border: "2px solid #19fd91", borderRadius: "10px", marginBottom: "14px" }}>
          <div style={{ color: "#aaa", fontSize: "0.85rem" }}>Your Code</div>
          <div style={{ fontSize: "2rem", fontWeight: 700, color: "#19fd91" }}>{result.code}</div>
        </div>
        {result.top?.map((t, i) => (
          <div key={i} style={{ marginBottom: "12px", padding: "12px", borderRadius: "10px", background: "rgba(255,255,255,0.04)" }}>
            <h4 style={{ color: "#19fd91", marginBottom: "6px" }}>{i + 1}. {t.letter} — {t.name}</h4>
            <p style={{ fontSize: "0.88rem", color: "#aaa", marginBottom: "8px" }}>{t.description}</p>
            {t.majors?.length > 0 && (
              <div style={{ marginBottom: "8px" }}>
                <b style={{ color: "#19fd91", fontSize: "0.85rem" }}>🎓 Majors:</b>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "5px" }}>
                  {t.majors.map((m, idx) => (
                    <span key={idx} style={{ background: "rgba(25,253,145,0.15)", padding: "4px 8px", borderRadius: "6px", fontSize: "0.8rem", color: "#19fd91" }}>{m}</span>
                  ))}
                </div>
              </div>
            )}
            {t.pathways?.length > 0 && (
              <div>
                <b style={{ color: "#19fd91", fontSize: "0.85rem" }}>🚀 Career Paths:</b>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "5px" }}>
                  {t.pathways.map((p, idx) => (
                    <span key={idx} style={{ background: "rgba(25,253,145,0.2)", padding: "4px 10px", borderRadius: "20px", fontSize: "0.8rem", color: "#19fd91" }}>{p}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
        {A.tip(getRiasecGrowthTip(result.top?.[0]?.letter))}
      </div>
    );
  }

  // ── 9. IQ ────────────────────────────────────────────────────────────
  // FIXED: previously this treated `result.score` as if it were the IQ
  // number. In reality `result.score` (or `result.correct`) is the RAW
  // number of correct answers, and `result.iq` is the actual computed IQ.
  // That mismatch caused the admin dashboard to show the wrong number and
  // a wrong classification bucket (e.g. showing "23" and "Below Average"
  // instead of "118" and "Above Average").
  if (testName.toLowerCase().includes("iq") && (result?.iq !== undefined || result?.score !== undefined)) {

    // ✅ Prefer the actual computed IQ. Fall back to result.score only for
    // older records saved before the `iq` field existed.
    const iqScore = result.iq ?? result.score;

    // ✅ Raw score (answers correct) is a separate number from IQ.
    const rawScore = result.rawScore ?? result.correct ?? result.score ?? 0;
    const TOTAL = result.total || 30;
    const accuracyPct = TOTAL ? ((rawScore / TOTAL) * 100).toFixed(1) : null;

    // ✅ Same 7-tier scoring table as IqQuizPage.getIQClassification, so a
    // fallback classification (only used if `result.classification` is
    // missing on an older record) always agrees with what the quiz itself
    // would say. Keep these two functions in sync if the table changes.
    const getClassification = (iq) =>
      iq >= 130 ? "Very Superior" :
      iq >= 120 ? "Superior" :
      iq >= 110 ? "Above Average" :
      iq >= 90  ? "Average" :
      iq >= 80  ? "Below Average" :
      iq >= 70  ? "Borderline" :
                  "Estimated Extremely Low";

    // ✅ Use the classification already saved with the result (matches what
    // the user actually saw) and only recompute if it's missing.
    const classification = result.classification || getClassification(iqScore);

    const getIQExplanation = (iq) => {
      if (iq >= 130) return "You have a very high level of intelligence.\n\nYou can quickly understand complex ideas, notice patterns easily, and think deeply about problems. You likely enjoy challenges that require strategy, logic, or creativity.\n\nIn real life, this means you can solve problems faster, learn new skills quickly, and adapt well in difficult situations.\n\nPeople with this level of IQ often do well in fields like technology, research, business, design, or leadership roles.\n\nJust remember — your ability is strong, but growth depends on how consistently you use and challenge your mind.";

      if (iq >= 120) return "You have a strong, well above-average level of intelligence.\n\nYou tend to grasp new concepts quickly and perform well on tasks that require analysis, logic, or problem-solving. You likely enjoy intellectual challenges and pick up new skills with relative ease.\n\nIn everyday life, this often translates into confident decision-making and efficient learning across a wide range of subjects.\n\nWith consistent practice, you can continue building on this strong foundation.";

      if (iq >= 110) return "You have above-average intelligence.\n\nYou can understand concepts faster than most people and usually perform well in problem-solving situations. You are good at logical thinking and can handle tasks that require planning and analysis.\n\nIn everyday life, you are likely able to make good decisions, learn new things efficiently, and handle responsibilities confidently.\n\nWith regular practice and focus, you can reach a very high level of performance in your studies or career.";

      if (iq >= 90) return "You have a balanced and practical level of intelligence.\n\nYou can manage daily tasks, decisions, and problem-solving without much difficulty. You may not always think very fast, but you are consistent and dependable.\n\nIf you regularly practice thinking skills like reading, reasoning, and learning new concepts, you can improve even more over time.";

      if (iq >= 80) return "Your score is in a somewhat lower-than-average range.\n\nYou can manage familiar, everyday tasks reasonably well, but new or abstract problems may sometimes take more time and effort to work through.\n\nThis is common and very workable — regular practice with reasoning and problem-solving exercises tends to help steadily over time.";

      if (iq >= 70) return "You may find some logical or complex thinking tasks a bit challenging.\n\nAt times, understanding new ideas or solving problems may take more effort or time. This is completely normal. With practice, you can improve your thinking ability step by step.\n\nStart with simple activities like puzzles, reading, or guided learning.";

      return "Right now, some thinking or problem-solving tasks may feel difficult.\n\nYou might struggle with understanding certain concepts or making quick decisions, but this does not define your ability or your future.\n\nImprovement comes from small, consistent efforts. With patience and consistency, you will definitely improve over time.";
    };

    const getIQTip = (iq) =>
      iq >= 130 ? "Challenge yourself with advanced problems and leadership roles." :
      iq >= 120 ? "Take on complex projects that stretch your analytical thinking." :
      iq >= 110 ? "Keep learning new skills and engage in analytical thinking." :
      iq >= 90  ? "Practice reasoning and problem-solving regularly." :
      iq >= 80  ? "Build steady habits around reading and simple logic exercises." :
      iq >= 70  ? "Start with simple puzzles and improve gradually." :
                  "Focus on basic cognitive exercises daily.";

    return (
      <div>
        <div style={{ fontSize: "0.88rem", color: "#555", marginBottom: "6px" }}>
          Estimated IQ Score
        </div>

        {A.hero(iqScore, classification)}

        {A.para(getIQExplanation(iqScore))}

        {A.tip(getIQTip(iqScore))}

        <div style={{ fontSize: "0.8rem", color: "#555", marginTop: "6px" }}>
          Raw: {rawScore} / {TOTAL}{accuracyPct !== null ? ` (${accuracyPct}%)` : ""}
        </div>

        <div style={{ fontSize: "0.75rem", color: "#555", marginTop: "10px", fontStyle: "italic" }}>
          This is an estimated score and is not a clinically validated IQ measurement.
        </div>
      </div>
    );
  }

  // ── 10. EQ ───────────────────────────────────────────────────────────
  if (testName.toLowerCase().includes("eq") && result?.score !== undefined) {
    const score = result.score;
    const getInterpretation = (s) =>
      s >= 84 ? "Very high emotional intelligence" :
      s >= 64 ? "High emotional intelligence"      :
      s >= 37 ? "Moderate emotional intelligence"  :
      s >= 17 ? "Low emotional intelligence"       : "Very low emotional intelligence";

    const getEQExplanation = (s) => {
      if (s >= 84) return "You are very good at understanding your emotions and staying calm even in difficult situations.\n\nYou also understand how others feel, which helps you build strong and meaningful relationships.\n\nPeople with this level of EQ usually do well in teamwork, leadership, and communication.";
      if (s >= 64) return "You handle your emotions well in most situations and can understand others.\n\nYou are generally good at maintaining relationships and dealing with everyday stress.\n\nWith a little more awareness and reflection, you can improve even further.";
      if (s >= 37) return "You understand emotions in many situations, but sometimes you may react quickly or feel confused.\n\nYou can improve by paying more attention to your emotions and how you respond to others.\n\nSmall changes can make a big difference over time.";
      if (s >= 17) return "You may find it difficult to manage emotions or understand others clearly.\n\nSometimes reactions may feel out of control or confusing.\n\nThe good part is — emotional intelligence can improve with practice and awareness.";
      return "You may struggle with understanding emotions and handling relationships right now.\n\nBut this is not permanent — with small daily efforts, you can improve step by step.";
    };

    const getEQTip = (s) =>
      s >= 84 ? "Keep building leadership and mentoring skills." :
      s >= 64 ? "Practice active listening and emotional awareness." :
      s >= 37 ? "Reflect on your emotions and improve communication." :
      s >= 17 ? "Start journaling and observing your reactions." : "Focus on basic emotional awareness and control exercises.";

    return (
      <div>
        <div style={{ fontSize: "0.88rem", color: "#555", marginBottom: "6px" }}>EQ Score</div>
        {A.hero(score, getInterpretation(score))}
        {A.para(getEQExplanation(score))}
        {A.tip(getEQTip(score))}
      </div>
    );
  }

  // ── 11. DR PHIL ──────────────────────────────────────────────────────
  if (testName.toLowerCase().includes("phil") && result?.score !== undefined) {
    const score = result.score;
    const total = result.totalScore || 100;
    const level = score < total * 0.4 ? "Low" : score < total * 0.7 ? "Average" : "High";

    const getInterpretation = () => {
      if (score < total * 0.4) return "Your score is in the lower range.\n\nThis means you may sometimes find it difficult to stay calm in stressful situations or make confident decisions. You might feel confused, overwhelmed, or depend on others when things get tough.\n\nBut this is completely normal — these skills can be improved with practice.\n\nWith small daily efforts, you can build confidence, emotional control, and better decision-making ability.";
      if (score < total * 0.7) return "Your score is in the average range.\n\nThis means you are able to handle most situations in a balanced way.\n\nSometimes you may feel unsure, stressed, or confused while making decisions, but overall you manage things reasonably well.\n\nYou have a good base — with a little more focus and practice, you can become much stronger mentally and emotionally.";
      return "Your score is in the high range.\n\nYou are good at handling your emotions and staying calm even in difficult situations.\n\nYou think clearly before making decisions and can manage stress better than most people.\n\nYou are likely seen as strong, stable, and dependable. Keep building on these strengths.";
    };

    const getStrengths = () =>
      score < total * 0.4
        ? ["You are aware that you need to improve", "You are open to learning and growing", "You try your best even when situations feel difficult"]
        : score < total * 0.7
        ? ["You handle situations in a balanced way", "You can adjust to different environments", "You understand people and situations fairly well"]
        : ["You stay calm under pressure", "You make clear and confident decisions", "You manage stress effectively", "People may rely on you in difficult situations"];

    const getWeaknesses = () =>
      score < total * 0.4
        ? ["You may struggle with confidence", "Stress may affect your decisions", "You may depend too much on others"]
        : score < total * 0.7
        ? ["You may overthink sometimes", "You may feel unsure in tough situations", "Stress can affect your performance occasionally"]
        : ["You may take too much responsibility", "You may get mentally tired (burnout)", "You may overthink important decisions"];

    const getThinkingStyle = () =>
      score < total * 0.4 ? "You rely more on emotions, which can make decision-making difficult sometimes." :
      score < total * 0.7 ? "You use both logic and emotions, but sometimes feel unsure." :
      "You balance logic and emotions very well, helping you make strong decisions.";

    const getImprovements = () =>
      score < total * 0.4
        ? ["Start making small decisions on your own", "Practice staying calm in simple stressful situations", "Avoid overthinking before acting", "Spend 5–10 minutes daily reflecting on your actions"]
        : score < total * 0.7
        ? ["Build confidence in decision-making", "Practice solving small problems daily", "Improve stress control techniques", "Set clear and simple goals"]
        : ["Maintain your emotional balance", "Take breaks to avoid burnout", "Continue learning and improving", "Support and guide others around you"];

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {A.hero(`${score} / ${total}`, `${level} range`, "Personality Test Score")}
        <div>
          <div style={{ fontWeight: 600, fontSize: "0.88rem", color: "#e0e0e0", marginBottom: "4px" }}>🧠 What Your Score Means</div>
          {A.para(getInterpretation())}
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: "0.88rem", color: "#e0e0e0", marginBottom: "6px" }}>✅ Strengths</div>
            <ul style={{ fontSize: "0.85rem", color: "#aaa", paddingLeft: "16px" }}>
              {getStrengths().map((s, i) => <li key={i} style={{ marginBottom: "4px" }}>{s}</li>)}
            </ul>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: "0.88rem", color: "#e0e0e0", marginBottom: "6px" }}>⚠️ Areas to Improve</div>
            <ul style={{ fontSize: "0.85rem", color: "#aaa", paddingLeft: "16px" }}>
              {getWeaknesses().map((w, i) => <li key={i} style={{ marginBottom: "4px" }}>{w}</li>)}
            </ul>
          </div>
        </div>
        <div>
          <div style={{ fontWeight: 600, fontSize: "0.88rem", color: "#e0e0e0", marginBottom: "4px" }}>🧩 Your Thinking Style</div>
          {A.para(getThinkingStyle())}
        </div>
        <div>
          <div style={{ fontWeight: 600, fontSize: "0.88rem", color: "#e0e0e0", marginBottom: "6px" }}>📈 How You Can Improve</div>
          <ul style={{ fontSize: "0.85rem", color: "#aaa", paddingLeft: "16px" }}>
            {getImprovements().map((imp, i) => <li key={i} style={{ marginBottom: "4px" }}>{imp}</li>)}
          </ul>
        </div>
        {A.tip("Your score shows your current level — not your limit. With regular practice, you can improve step by step.")}
      </div>
    );
  }

  // ── 12. DIAGRAM ANALYSIS ─────────────────────────────────────────────
  if (testName === "Diagram Analysis Test" && result?.selectedShape) {
    const details = {
      1: {
        score:"A",
        explanation: "You are someone who enjoys action, movement, and new experiences.\n\nYou don't like routine work — you feel more alive when things are changing and exciting. You are likely to take initiative and step forward rather than wait.\n\nYou enjoy being involved in activities and often prefer roles where you can lead, act, or explore.",
        strengths: "✔ Energetic and proactive\n✔ Good in dynamic environments\n✔ Takes initiative easily\n✔ Adapts quickly to change",
        tip: "Try to balance excitement with consistency. Sometimes routine is necessary for long-term success. Focus on finishing what you start.",
      },
      2: {
        score:"A+",
        explanation: "You are a deep thinker who understands yourself well.\n\nYou prefer meaningful conversations over casual talk. You are emotionally aware and value strong, genuine relationships.\n\nYou may not open up quickly, but when you do, your connections are deep and real.",
        strengths: "✔ Emotionally aware\n✔ Deep thinker\n✔ Strong personal values\n✔ Builds meaningful relationships",
        tip: "Don't isolate yourself too much. Try to open up gradually and connect with more people. Balance depth with social interaction.",
      },
      3: {
        score:"A",
        explanation: "You are practical, stable, and balanced.\n\nYou value simplicity and reliability. People trust you because you are consistent and grounded.\n\nYou prefer real, meaningful things over trends or unnecessary complexity.",
        strengths: "✔ Reliable and stable\n✔ Practical thinker\n✔ Calm and balanced\n✔ Trustworthy personality",
        tip: "Try to step out of your comfort zone sometimes. Explore new ideas and experiences. Growth happens when you challenge yourself.",
      },
      4: {
        score:"A+",
        explanation: "You are cheerful, free-spirited, and love spontaneity.\n\nYou enjoy trying new things and dislike restrictions. You bring energy and positivity into situations.\n\nYou are naturally curious and open-minded.",
        strengths: "✔ Positive and energetic\n✔ Open to new experiences\n✔ Fun to be around\n✔ Curious mindset",
        tip: "Be mindful of consistency. Too much spontaneity can lead to lack of focus. Balance fun with responsibility.",
      },
      5: {
          score:"A",
        explanation: "You are emotional, imaginative, and deeply connected to your feelings.\n\nYou value creativity, dreams, and emotional experiences. You may feel things more strongly than others.\n\nYour inner world is rich and expressive.",
        strengths: "✔ Creative and imaginative\n✔ Emotionally deep\n✔ Expressive personality\n✔ Strong intuition",
        tip: "Try not to let emotions control every decision. Balance feelings with logic. Stay grounded in practical reality when needed.",
      },
    }[result.selectedShape] || {};

    return (
      <div>
        <div style={{ fontSize: "0.85rem", color: "#555", marginBottom: "6px" }}>Selected Shape: {result.selectedShape}</div>
        <div style={{ fontSize: "1rem", fontWeight: 700, color: "#19fd91", marginBottom: "10px" }}>{result.personality}</div>
        {details.explanation && A.para(details.explanation)}
        {details.strengths && <>{A.sectionHead("What you're strong at:")} {A.para(details.strengths)}</>}
        {details.tip && A.tip(details.tip)}
      </div>
    );
  }

  // ── 13. GEOMETRY PERSONALITY ─────────────────────────────────────────
  if (testName === "Geometry Personality Test" && result?.selectedShape) {
    const getShapeMeaning = (s) => ({
      Triangle:  "You are a goal-driven and leadership-oriented personality. You naturally prefer taking control, setting direction, and achieving visible success. In real life, you may often take initiative in group situations and prefer being in charge rather than following others.",
      Square:    "You are a structured and disciplined personality. You prefer order, planning, and clarity in life. In real situations, you are dependable, consistent, and focused on completing tasks in a systematic way without unnecessary chaos.",
      Rectangle: "You are in a phase of growth and transition. You are open-minded, flexible, and constantly learning. In real life, you adapt easily to new environments and are comfortable exploring change and uncertainty.",
      Circle:    "You are a warm, emotional, and people-oriented personality. You value relationships, harmony, and emotional connection. In real life, you are often seen as caring, supportive, and someone people trust for emotional comfort.",
      Squiggle:  "You are a creative, energetic, and unconventional thinker. You enjoy freedom, originality, and expressing yourself in unique ways. In real life, you often bring new ideas, spontaneity, and creativity into situations.",
    }[s] || "");

    const getRealLife = (s) => ({
      Triangle:  "This suggests you perform best in leadership roles, competitive environments, and situations where decision-making is required.",
      Square:    "This suggests you perform best in structured environments, routine tasks, and roles that require accuracy and responsibility.",
      Rectangle: "This suggests you perform best in learning environments, new experiences, and situations requiring adaptability.",
      Circle:    "This suggests you perform best in team environments, social settings, and roles involving communication or support.",
      Squiggle:  "This suggests you perform best in creative environments, innovation-based work, and situations requiring imagination.",
    }[s] || "");

    const getGrowthTip = (s) => ({
      Triangle:  "Learn to balance leadership with listening. Not every situation needs control — sometimes collaboration brings better results.",
      Square:    "Try to be flexible with change. Not everything will go as planned, and adapting will help you grow faster.",
      Rectangle: "Stay consistent while exploring new things. Too much change without direction can slow your progress.",
      Circle:    "Don't ignore your own needs while caring for others. Set boundaries and take care of yourself too.",
      Squiggle:  "Focus on completing what you start. Creativity is powerful, but discipline turns ideas into results.",
    }[s] || "");

    return (
      <div>
        <div style={{ fontWeight: 700, color: "#19fd91", fontSize: "1.05rem", marginBottom: "10px" }}>Geometry Personality Test Report</div>
        <div style={{ background: "rgba(25,253,145,0.08)", border: "1px solid rgba(25,253,145,0.25)", borderRadius: "14px", padding: "1.2rem", textAlign: "center", marginBottom: "14px" }}>
          <div style={{ fontSize: "0.85rem", color: "#555" }}>Selected Shape</div>
          <div style={{ fontSize: "2rem", fontWeight: 800, color: "#19fd91", marginTop: "6px" }}>{result.selectedShape}</div>
        </div>
        {A.infoBox(<>
          <div style={{ fontWeight: 600, marginBottom: "8px", color: "#e0e0e0" }}>🧠 Personality Interpretation</div>
          {A.para(getShapeMeaning(result.selectedShape))}
        </>)}
        {A.infoBox(<>
          <div style={{ fontWeight: 600, marginBottom: "6px", color: "#e0e0e0" }}>🔗 How This Relates to You</div>
          {A.para(getRealLife(result.selectedShape))}
        </>)}
        {result.traits && A.infoBox(<>
          <div style={{ fontWeight: 600, marginBottom: "6px", color: "#e0e0e0" }}>📊 Trait Breakdown</div>
          {A.para(result.traits)}
        </>)}
        {A.tip(getGrowthTip(result.selectedShape))}
      </div>
    );
  }

  // ── 14. SOCIAL QUOTIENT (SQ) ─────────────────────────────────────────
  if (testName === "Social Quotient Test" && result?.score !== undefined) {
    const s = result.score;
    const getSqInterpretation = (score) => {
      if (score >= 65) return {
        title: "Very High Social Intelligence",
        strengths: "You are naturally very comfortable around people and can easily understand how others feel.\n\nYou respond calmly and respectfully in conversations, even in difficult situations.\n\nPeople likely trust you and feel safe sharing things with you because you listen and communicate clearly.\n\nYou are able to adjust your communication style depending on the person and situation, which makes you socially adaptable.",
        growthTip: "Continue building confidence in challenging conversations or leadership situations. Make sure you don't always adjust yourself just to please others — your own boundaries are important too.",
      };
      if (score >= 50) return {
        title: "High / Healthy Social Skills",
        strengths: "You handle social situations well and can communicate your thoughts clearly.\n\nYou are able to understand others' feelings most of the time and respond appropriately.\n\nYou are a good listener and can maintain healthy and meaningful conversations.\n\nPeople likely see you as friendly, approachable, and easy to talk to.",
        growthTip: "Work on staying calm when receiving criticism or feedback. Practice being more confident in uncomfortable or new social situations.",
      };
      if (score >= 35) return {
        title: "Moderate Social Skills",
        strengths: "You can manage basic social interactions and connect with people in familiar situations.\n\nYou understand others to some extent, but may sometimes feel unsure about how to respond.\n\nYou have the ability to build strong social skills — you just need more confidence and practice.",
        growthTip: "Practice active listening and focus fully when someone is speaking. Improve eye contact and confidence when talking to new people.",
      };
      return {
        title: "Needs Social Skill Development",
        strengths: "You may prefer quiet environments or smaller groups where you feel more comfortable.\n\nYou tend to think before speaking, which can actually be a strength when used well.",
        growthTip: "Practice active listening. Improve eye contact and confidence. Try to notice small emotional signals like tone, expressions, and body language.",
      };
    };

    const interp = getSqInterpretation(s);
    return (
      <div>
        <div style={{ fontSize: "0.85rem", color: "#555", marginBottom: "5px" }}>SQ Score</div>
        {A.hero(s, interp.title)}
        {A.sectionHead("What you're strong at:")}
        {A.para(interp.strengths)}
        {A.tip(interp.growthTip)}
      </div>
    );
  }

  // ── 15. PERCEPTION PERSONALITY ───────────────────────────────────────
  if (testName === "Perception Personality Test" && result?.selectedWords) {
    return (
      <div>
        <div style={{ fontSize: "0.85rem", color: "#555", marginBottom: "10px" }}>Perception Personality</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "14px" }}>
          {result.selectedWords.map((w, i) => (
            <div key={i} style={{ padding: "6px 12px", background: "#19fd91", color: "#000", borderRadius: "20px", fontSize: "0.8rem", fontWeight: 600 }}>
              {w}
            </div>
          ))}
        </div>
        <div>
          {result.selectedWords.map((w, i) => (
            <div key={i} style={{ marginBottom: "10px", fontSize: "0.88rem", lineHeight: 1.5 }}>
              <strong style={{ color: "#19fd91" }}>{w}:</strong>{" "}
              <span style={{ color: "#aaa" }}>{WORD_MEANINGS[w.toLowerCase()] || "This trait reflects a unique part of your personality."}</span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: "12px", fontSize: "0.88rem", lineHeight: 1.6, color: "#555" }}>
          These words represent how your mind naturally observes people and situations. They reflect your inner personality, values, and emotional thinking style.
        </div>
        {A.tip("Try to understand people beyond first impressions. Your perception is strong, but combining it with deeper understanding will make your decisions even better.")}
      </div>
    );
  }

  // ── 16. PERSONALITY QUOTIENT (PQ) ────────────────────────────────────
  if (testName === "Personality Quotient Test (PQ)" && result?.score !== undefined) {
    const s = result.score;
    const getPqInterpretation = (score) => {
      if (score >= 40) return {
        title: "Excellent People Understanding",
        explanation: "You have a very strong ability to understand people.\n\nYou can easily notice how others feel just by observing their tone, expressions, or behavior. You are good at reading situations and adjusting your response accordingly.\n\nYou probably communicate in a way that makes others feel comfortable and understood. Because of this, people may trust you easily and enjoy talking to you.\n\nYou are also able to handle social and professional situations confidently. Whether it's teamwork, leadership, or conversations — you naturally perform well.\n\nThis is a powerful skill in real life, especially in careers that involve people, communication, or decision-making.",
        strengths: "✔ You understand emotions quickly\n✔ You read body language and behavior well\n✔ You build trust easily with people\n✔ You handle social situations confidently",
        growthTip: "Keep challenging yourself in leadership roles. Don't always adjust yourself for others — your boundaries matter. Use your skills to guide and support people positively.",
      };
      if (score >= 30) return {
        title: "Strong Social Awareness",
        explanation: "You have a good understanding of people and relationships.\n\nMost of the time, you can understand how others feel and respond in a reasonable way. You are able to communicate clearly and maintain healthy conversations.\n\nYou are someone who can build meaningful connections, whether in friendships, college, or work environments.\n\nSometimes, you may miss very small emotional signals, but overall your social understanding is strong and reliable.\n\nWith a little more awareness and practice, you can become excellent at reading people.",
        strengths: "✔ You understand common emotions\n✔ You communicate clearly\n✔ You build stable relationships\n✔ You are approachable and friendly",
        growthTip: "Focus on small emotional signals like tone and expressions. Practice deeper listening instead of just responding. Be more confident in expressing your thoughts openly.",
      };
      if (score >= 20) return {
        title: "Developing Social Skills",
        explanation: "You have basic social understanding, but there is room to grow.\n\nYou can handle simple conversations and situations, especially with familiar people. However, in new or complex situations, you may feel unsure about how to respond.\n\nSometimes you might not fully understand what others are feeling, or you may take time to react.\n\nThe good part is — you have strong potential to improve. Social intelligence is not fixed, it improves with practice.\n\nWith regular effort, you can become much more confident and comfortable in social situations.",
        strengths: "✔ You can handle basic conversations\n✔ You can connect with familiar people\n✔ You have good potential to improve",
        growthTip: "Practice active listening — focus fully when someone talks. Improve eye contact and confidence. Observe how people express emotions through body language.",
      };
      return {
        title: "Needs Improvement",
        explanation: "You may find social situations a bit challenging right now.\n\nUnderstanding emotions, body language, or reactions of others might feel confusing sometimes. You may also feel uncomfortable starting or continuing conversations.\n\nBut this is completely okay — social skills are learned, not fixed.\n\nMany people improve these skills over time by practicing simple habits daily.\n\nYou can definitely improve your confidence and understanding step by step.",
        strengths: "✔ You think before speaking\n✔ You may prefer calm and quiet environments",
        growthTip: "Start with small daily conversations. Practice eye contact and clear speaking. Observe how people behave and react in different situations. With consistency, your social skills will improve.",
      };
    };

    const interp = getPqInterpretation(s);
    return (
      <div>
        <div style={{ fontSize: "0.85rem", color: "#555", marginBottom: "5px" }}>PQ Score</div>
        {A.hero(s, interp.title)}
        {A.para(interp.explanation)}
        {A.sectionHead("What you're strong at:")}
        {A.para(interp.strengths)}
        {A.tip(interp.growthTip)}
      </div>
    );
  }

  // ── 17. GRE ──────────────────────────────────────────────────────────
  if (testName.toLowerCase().includes("gre") && result?.score !== undefined) {
    const total = Number(result.total) || 0;
    const score = Number(result.score) || 0;
    const pct = result.percentage || (total ? Math.round((score / total) * 100) + "%" : "N/A");
    return (
      <div>
        <div style={{ fontWeight: 700, color: "#19fd91", fontSize: "1.05rem", marginBottom: "6px" }}>{testName}</div>
        <div style={{ fontWeight: 600, fontSize: "0.95rem", color: "#e0e0e0" }}>
          Score: {score} / {total}
          <span style={{ fontSize: "0.9rem", color: "#aaa", marginLeft: "8px" }}>({pct})</span>
        </div>
      </div>
    );
  }

  // ── GENERIC FALLBACK ──────────────────────────────────────────────────
  const entries = Object.entries(result);
  if (entries.length === 0) return <p style={{ color: "#aaa" }}>No details available.</p>;

  return (
    <div>
      <div style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em", color: "#555", marginBottom: "10px" }}>
        Result Details
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
        {entries.map(([k, v], i) => (
          <div key={i} style={{ background: "#1e1e1e", border: "1px solid #2a2a2a", borderRadius: "8px", padding: "10px 12px" }}>
            <div style={{ fontSize: "11px", color: "#555", marginBottom: "3px", textTransform: "uppercase" }}>{k}</div>
            <div style={{ fontSize: "13px", fontWeight: 500, color: "#e0e0e0", wordBreak: "break-word" }}>
              {typeof v === "object" ? JSON.stringify(v) : String(v)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

function AdminDashboard({ onLogout }) {
  const [activeSection, setActiveSection] = useState("dashboard"); // "dashboard" | "users"
  const [userSectionQuery, setUserSectionQuery] = useState("");
  const [exporting, setExporting] = useState(false);
  const [users, setUsers] = useState([]);
  const [results, setResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [deletingId, setDeletingId] = useState(null); // tracks which user is currently being deleted


  useEffect(() => { fetchUsers(); fetchResults(); }, []);

  useEffect(() => {
    if (selectedUser) { fetchResults(); setSelectedTest(null); }
  }, [selectedUser]);

  // FIXED (M6): these three requests previously hit a hardcoded
  // "http://localhost:5009/api/..." endpoint. That works only when the
  // admin dashboard happens to be opened on the same machine running the
  // backend on port 5009 — anywhere else (production domain, the VPS IP,
  // a teammate's laptop) the request either fails outright or silently
  // gets sent to the wrong host, alongside the admin's Bearer token.
  // Using the shared getApiBaseUrl() resolves this correctly for every
  // environment (psyx.io, the VPS IP, and localhost) the same way
  // AdminLogin.js already does.

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${getApiBaseUrl()}/auth/admin/users`, {
        credentials: "include",
      });
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : data.users || []);
    } catch (err) { console.error(err); }
  };

  const fetchResults = async () => {
    try {
      const res = await fetch(`${getApiBaseUrl()}/auth/admin/results`, {
        credentials: "include",
      });
      const data = await res.json();
      setResults(Array.isArray(data) ? data : data.results || []);
    } catch (err) { console.error(err); }
  };

  // ── DELETE A USER (ADMIN ONLY) ───────────────────────────────────────
  const deleteUser = async (user) => {
    const confirmed = window.confirm(
      `Delete ${user.fullname}? This will permanently remove this user and all their test results. This cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingId(user._id);
    try {
      const res = await fetch(`${getApiBaseUrl()}/auth/admin/users/${user._id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.msg || "Delete failed");
      }

      // Update local state immediately so the UI reflects the deletion
      setUsers((prev) => prev.filter((u) => u._id !== user._id));
      setResults((prev) => prev.filter((r) => r.user?._id !== user._id));

      if (selectedUser?._id === user._id) {
        setSelectedUser(null);
        setSelectedTest(null);
      }
    } catch (err) {
      console.error(err);
      alert("Failed to delete user. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  // ── ONLY CHANGE: deduplicate by testName, keep latest date ──────────
  const filteredResults = (() => {
    if (!selectedUser) return [];
    const userResults = results.filter(
      (r) => r.user?._id === selectedUser._id || r.user?.email === selectedUser.email
    );
    // For duplicate test names, keep only the latest (by date)
    const latestMap = new Map();
    userResults.forEach((r) => {
      const existing = latestMap.get(r.testName);
      if (!existing || new Date(r.date) > new Date(existing.date)) {
        latestMap.set(r.testName, r);
      }
    });
    return Array.from(latestMap.values());
  })();
  // ────────────────────────────────────────────────────────────────────

  // FIXED: for IQ tests, `score`/`correct` on the record is the RAW answer
  // count, not the IQ. Show the actual computed IQ (`iq`) here instead, so
  // the SCORE column in the table matches the IQ shown in the detail panel.
  //
  // FIXED (Career Interest Assessment): results shaped like
  // { scores: [...], topThree: [{title, score}, ...] } don't have a plain
  // `score`/`marks`/`totalScore` field, so this used to fall through to
  // `0`. Now it falls back to the top-ranked category's score so the
  // table shows something meaningful instead of a flat 0.
  const getScore = (res) => {
    const testName = res.testName?.toLowerCase() || "";
    const result = res.result || res;

    if (testName.includes("iq")) {
      return result?.iq ?? res.iq ?? result?.score ?? res.score ?? 0;
    }

    const directScore = res.score ?? res.marks ?? res.totalScore ??
      res.result?.score ?? res.result?.marks;
    if (directScore !== undefined && directScore !== null) return directScore;

    // Career Interest Assessment (topThree/scores category format)
    if (Array.isArray(result?.topThree) && result.topThree.length > 0) {
      return result.topThree[0].score ?? 0;
    }
    if (Array.isArray(result?.scores) && result.scores.length > 0) {
      const top = [...result.scores].sort((a, b) => (b.score || 0) - (a.score || 0))[0];
      return top?.score ?? 0;
    }

    return 0;
  };

  const formatDateTime = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString();
  };

  const getAvatarColor = (name) => {
    const colors = ["#1a7a4a","#2d6a8a","#6a3a8a","#8a5a1a","#1a5a6a","#4a6a1a","#6a1a4a","#3a4a8a"];
    return colors[(name?.charCodeAt(0) || 0) % colors.length];
  };

  // Extracts a creation timestamp for a user, falling back to the
  // timestamp embedded in a Mongo ObjectId if createdAt isn't present.
  // Used only as a fallback for users who haven't taken any test yet.
  const getUserTimestamp = (u) => {
    if (u.createdAt) return new Date(u.createdAt).getTime();
    if (u._id && typeof u._id === "string" && u._id.length >= 8) {
      return parseInt(u._id.substring(0, 8), 16) * 1000;
    }
    return 0;
  };

  // ── NEW: find the timestamp of a user's most recently taken test ────
  // Looks through the `results` array (all users' results) and returns
  // the latest `date` among the results that belong to this user.
  // Returns 0 if the user hasn't taken any test yet.
  const getUserLatestTestTimestamp = (u) => {
    let latest = 0;
    for (const r of results) {
      const belongsToUser = r.user?._id === u._id || r.user?.email === u.email;
      if (!belongsToUser) continue;
      const t = new Date(r.date).getTime();
      if (!isNaN(t) && t > latest) latest = t;
    }
    return latest;
  };

  const filteredUsers = users
    .filter(
      (u) =>
        u.fullname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      const aLatestTest = getUserLatestTestTimestamp(a);
      const bLatestTest = getUserLatestTestTimestamp(b);

      // If both users have taken at least one test, sort by whoever
      // took a test most recently.
      if (aLatestTest && bLatestTest) return bLatestTest - aLatestTest;

      // A user who has taken a test always ranks above one who hasn't.
      if (aLatestTest && !bLatestTest) return -1;
      if (!aLatestTest && bLatestTest) return 1;

      // Neither has taken a test yet — fall back to signup recency.
      return getUserTimestamp(b) - getUserTimestamp(a);
    });

  const uniqueTests = new Set(results.map((r) => r.testName)).size;

  // ── USERS SECTION: all users, newest registration first ─────────────
  const getUserTestStats = (u) => {
    const own = results.filter((r) => r.user?._id === u._id || r.user?.email === u.email);
    const names = [...new Set(own.map((r) => r.testName).filter(Boolean))];
    return { count: own.length, names, latest: getUserLatestTestTimestamp(u) };
  };

  const usersByRegistration = [...users].sort((a, b) => getUserTimestamp(b) - getUserTimestamp(a));

  const sectionUsers = usersByRegistration.filter((u) => {
    const q = userSectionQuery.toLowerCase();
    return (
      u.fullname?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.phone?.includes(q) ||
      u.city?.toLowerCase().includes(q)
    );
  });

  // Always exports every user (ignores the search box), newest first.
  const downloadUsersExcel = async () => {
    setExporting(true);
    try {
      const { default: ExcelJS } = await import("exceljs");
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("Users");

      sheet.columns = [
        { header: "S.No", key: "sno", width: 7 },
        { header: "Full Name", key: "fullname", width: 26 },
        { header: "Email", key: "email", width: 32 },
        { header: "Phone", key: "phone", width: 14 },
        { header: "City", key: "city", width: 16 },
        { header: "Study Preference", key: "study", width: 17 },
        { header: "Email Verified", key: "verified", width: 14 },
        { header: "Registered On", key: "registered", width: 22, style: { numFmt: "dd-mmm-yyyy hh:mm AM/PM" } },
        { header: "Tests Taken", key: "testsCount", width: 12 },
        { header: "Last Test On", key: "lastTest", width: 22, style: { numFmt: "dd-mmm-yyyy hh:mm AM/PM" } },
        { header: "Tests Attended", key: "testNames", width: 60 },
      ];

      usersByRegistration.forEach((u, i) => {
        const stats = getUserTestStats(u);
        const regTs = getUserTimestamp(u);
        sheet.addRow({
          sno: i + 1,
          fullname: u.fullname || "",
          email: u.email || "",
          phone: u.phone || "",
          city: u.city || "",
          study: u.study_preference || "",
          verified: u.isVerified ? "Yes" : "No",
          registered: regTs ? new Date(regTs) : "",
          testsCount: stats.count,
          lastTest: stats.latest ? new Date(stats.latest) : "",
          testNames: stats.names.join(", "),
        });
      });

      sheet.getRow(1).font = { bold: true };
      sheet.views = [{ state: "frozen", ySplit: 1 }];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `psyx-users-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Failed to generate the Excel file. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={st.root}>

      {/* ─── SIDEBAR ──────────────────────────────────────────────── */}
      <aside style={st.sidebar}>
        <div style={{ fontWeight: 800, color: "#19fd91", fontSize: "1.1rem", padding: "4px 12px 18px" }}>
          PsyX Admin
        </div>
        {[
          { key: "dashboard", icon: "📊", label: "Dashboard" },
          { key: "users", icon: "👥", label: "Users", badge: users.length },
        ].map(({ key, icon, label, badge }) => (
          <div
            key={key}
            style={{
              ...st.navItem,
              background: activeSection === key ? "rgba(25,253,145,0.10)" : "transparent",
              color: activeSection === key ? "#19fd91" : "#ccc",
            }}
            onClick={() => { setActiveSection(key); setSelectedUser(null); setSelectedTest(null); }}
          >
            <span style={st.navIcon}>{icon}</span>
            <span style={{ flex: 1 }}>{label}</span>
            {badge !== undefined && <span style={st.navBadge}>{badge}</span>}
          </div>
        ))}
        {onLogout && (
          <div style={st.sidebarFooter}>
            <div style={st.navItem} onClick={onLogout}>
              <span style={st.navIcon}>🚪</span> Logout
            </div>
          </div>
        )}
      </aside>

      {/* ─── USERS SECTION ────────────────────────────────────────── */}
      {activeSection === "users" && (
        <main style={st.main}>
          <div style={st.card}>
            <div style={st.cardHeader}>
              <div style={st.cardTitle}>
                <span style={{ marginRight: 8 }}>👥</span> All Users
                <span style={{ ...st.navBadge, marginLeft: 10 }}>{users.length}</span>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input
                  placeholder="Search name, email, phone, city..."
                  value={userSectionQuery}
                  onChange={(e) => setUserSectionQuery(e.target.value)}
                  style={{ ...st.searchInput, width: 240 }}
                />
                <button
                  style={{ ...st.viewBtn, opacity: exporting || users.length === 0 ? 0.6 : 1 }}
                  disabled={exporting || users.length === 0}
                  onClick={downloadUsersExcel}
                >
                  {exporting ? "Preparing…" : "⬇ Download Excel"}
                </button>
              </div>
            </div>
            <div style={{ fontSize: "0.78rem", color: "#555", marginBottom: 10 }}>
              Sorted by registration date — most recent first.
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={st.table}>
                <thead>
                  <tr>
                    {["#", "Name", "Email", "Phone", "City", "Study Pref.", "Verified", "Registered On", "Tests"].map((h) => (
                      <th key={h} style={{ ...st.th, whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sectionUsers.map((u, i) => {
                    const regTs = getUserTimestamp(u);
                    return (
                      <tr key={u._id || i} style={st.tr}>
                        <td style={{ ...st.td, color: "#555" }}>{i + 1}</td>
                        <td style={{ ...st.td, fontWeight: 500, whiteSpace: "nowrap" }}>{u.fullname}</td>
                        <td style={{ ...st.td, color: "#777" }}>{u.email}</td>
                        <td style={{ ...st.td, whiteSpace: "nowrap" }}>{u.phone || "—"}</td>
                        <td style={st.td}>{u.city || "—"}</td>
                        <td style={st.td}>{u.study_preference || "—"}</td>
                        <td style={{ ...st.td, color: u.isVerified ? "#19fd91" : "#ff6b6b" }}>
                          {u.isVerified ? "Yes" : "No"}
                        </td>
                        <td style={{ ...st.td, color: "#777", fontSize: "0.82rem", whiteSpace: "nowrap" }}>
                          {regTs ? formatDateTime(regTs) : "N/A"}
                        </td>
                        <td style={{ ...st.td, color: "#19fd91", fontWeight: 700 }}>{getUserTestStats(u).count}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {sectionUsers.length === 0 && (
                <p style={{ color: "#555", textAlign: "center", padding: "1.5rem 0" }}>No users found.</p>
              )}
            </div>
          </div>
        </main>
      )}

      {/* ─── MAIN ─────────────────────────────────────────────────── */}
      {activeSection === "dashboard" && (
      <main style={st.main}>

        {/* STATS ROW */}
        <div style={st.statsRow}>
          {[
            { icon: "👥", value: users.length,  label: "Total Users"   },
            { icon: "📋", value: results.length, label: "Total Results" },
            { icon: "✏️", value: uniqueTests,    label: "Tests Taken"  },
          ].map(({ icon, value, label }) => (
            <div key={label} style={st.statCard}>
              <div style={st.statIconWrap}>{icon}</div>
              <div>
                <div style={st.statValue}>{value}</div>
                <div style={st.statLabel}>{label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ─── USERS TABLE ──────────────────────────────────────── */}
        {!selectedUser && (
          <div style={st.card}>
            <div style={st.cardHeader}>
              <div style={st.cardTitle}><span style={{ marginRight: 8 }}>👥</span> Users</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={st.searchInput}
                />
                <button
                  style={{ ...st.viewBtn, opacity: exporting || users.length === 0 ? 0.6 : 1 }}
                  disabled={exporting || users.length === 0}
                  onClick={downloadUsersExcel}
                >
                  {exporting ? "Preparing…" : "⬇ Download Users"}
                </button>
              </div>
            </div>
            <table style={{ ...st.table, tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: 44 }} />
                <col style={{ width: "32%" }} />
                <col style={{ width: "34%" }} />
                <col style={{ width: 220 }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={st.th}>#</th>
                  <th style={st.th}>NAME</th>
                  <th style={st.th}>EMAIL</th>
                  <th style={st.th}>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user, index) => (
                  <tr key={index} style={st.tr}>
                    <td style={{ ...st.td, color: "#555" }}>{index + 1}</td>
                    <td style={st.td}>
                      <div style={st.nameCell}>
                        <div style={{ ...st.avatarCircle, background: getAvatarColor(user.fullname), flexShrink: 0 }}>
                          {user.fullname?.charAt(0)?.toUpperCase()}
                        </div>
                        <span style={{ fontWeight: 500, color: "#e0e0e0", ...st.truncate }} title={user.fullname}>
                          {user.fullname}
                        </span>
                      </div>
                    </td>
                    <td style={{ ...st.td, color: "#777", ...st.truncate }} title={user.email}>
                      {user.email}
                    </td>
                    <td style={st.td}>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button style={st.viewBtn} onClick={() => setSelectedUser(user)}>
                          View Results
                        </button>
                        <button
                          style={{
                            ...st.deleteBtn,
                            opacity: deletingId === user._id ? 0.6 : 1,
                            cursor: deletingId === user._id ? "not-allowed" : "pointer",
                          }}
                          disabled={deletingId === user._id}
                          onClick={() => deleteUser(user)}
                        >
                          {deletingId === user._id ? "Deleting…" : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ─── USER DETAIL VIEW ──────────────────────────────────── */}
        {selectedUser && (
          <>
            <div style={st.card}>
              <div style={st.cardHeader}>
                <div style={st.cardTitle}>
                  <div style={{ ...st.avatarCircle, background: getAvatarColor(selectedUser.fullname), marginRight: 10 }}>
                    {selectedUser.fullname?.charAt(0)?.toUpperCase()}
                  </div>
                  {selectedUser.fullname}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    style={{
                      ...st.deleteBtn,
                      opacity: deletingId === selectedUser._id ? 0.6 : 1,
                      cursor: deletingId === selectedUser._id ? "not-allowed" : "pointer",
                    }}
                    disabled={deletingId === selectedUser._id}
                    onClick={() => deleteUser(selectedUser)}
                  >
                    {deletingId === selectedUser._id ? "Deleting…" : "Delete User"}
                  </button>
                  <button style={st.backBtn} onClick={() => { setSelectedUser(null); setSelectedTest(null); }}>
                    ← Back to Users
                  </button>
                </div>
              </div>
              <div style={st.metaRow}>
                <div style={st.metaItem}>
                  <span style={st.metaLabel}>Email</span>
                  <span style={st.metaValue}>{selectedUser.email}</span>
                </div>
                <div style={st.metaItem}>
                  <span style={st.metaLabel}>Phone</span>
                  <span style={st.metaValue}>{selectedUser.phone || "N/A"}</span>
                </div>
                <div style={st.metaItem}>
                  <span style={st.metaLabel}>Tests Taken</span>
                  <span style={{ ...st.metaValue, color: "#19fd91", fontWeight: 700 }}>{filteredResults.length}</span>
                </div>
              </div>
            </div>

            <div style={st.card}>
              <div style={st.cardHeader}>
                <div style={st.cardTitle}><span style={{ marginRight: 8 }}>📊</span> Tests Attended</div>
              </div>
              {filteredResults.length === 0 ? (
                <p style={{ color: "#555", textAlign: "center", padding: "1.5rem 0" }}>No results found for this user.</p>
              ) : (
                <table style={st.table}>
                  <thead>
                    <tr>
                      <th style={st.th}>#</th>
                      <th style={st.th}>TEST NAME</th>
                      <th style={st.th}>SCORE</th>
                      <th style={st.th}>DATE & TIME</th>
                      <th style={st.th}>ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredResults.map((res, index) => (
                      <tr key={index} style={{ ...st.tr, background: selectedTest === res ? "rgba(25,253,145,0.04)" : "transparent" }}>
                        <td style={{ ...st.td, color: "#555", width: 36 }}>{index + 1}</td>
                        <td style={{ ...st.td, fontWeight: 500 }}>{res.testName}</td>
                        <td style={{ ...st.td, color: "#19fd91", fontWeight: 700 }}>{getScore(res)}</td>
                        <td style={{ ...st.td, color: "#777", fontSize: "0.82rem" }}>{formatDateTime(res.date)}</td>
                        <td style={st.td}>
                          <button
                            style={{ ...st.viewBtn, background: selectedTest === res ? "rgba(25,253,145,0.28)" : "rgba(25,253,145,0.12)" }}
                            onClick={() => setSelectedTest(selectedTest === res ? null : res)}
                          >
                            {selectedTest === res ? "Hide" : "Details"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* ─── DETAIL PANEL ──────────────────────────────────── */}
            {selectedTest && (
              <div style={st.card}>
                <div style={st.cardHeader}>
                  <div style={st.cardTitle}>
                    <span style={{ marginRight: 8 }}>🧪</span>
                    {selectedTest.testName}
                  </div>
                  <button style={st.backBtn} onClick={() => setSelectedTest(null)}>✕ Close</button>
                </div>

                <div style={st.metaRow}>
                  <div style={st.metaItem}>
                    <span style={st.metaLabel}>Score</span>
                    <span style={{ ...st.metaValue, color: "#19fd91", fontSize: "1.5rem", fontWeight: 800 }}>
                      {getScore(selectedTest)}
                    </span>
                  </div>
                  <div style={st.metaItem}>
                    <span style={st.metaLabel}>Date & Time</span>
                    <span style={st.metaValue}>{formatDateTime(selectedTest.date)}</span>
                  </div>
                </div>

                <div style={{ height: "1px", background: "#1e1e1e", margin: "16px 0" }} />

                <div style={st.detailLabel}>Detailed Result</div>
                <div style={st.detailBox}>
                  {renderDetailedResult(selectedTest)}
                </div>
              </div>
            )}
          </>
        )}
      </main>
      )}
    </div>
  );
}

// ── STYLES ───
const st = {
  root: {
    display: "flex", minHeight: "100vh", background: "#0d0d0d",
    color: "#e0e0e0", fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif", fontSize: "14px",
  },
  sidebar: {
    width: 200, background: "#111111", borderRight: "1px solid #1c1c1c",
    padding: "16px 10px", display: "flex", flexDirection: "column",
    position: "sticky", top: 0, height: "100vh", flexShrink: 0,
  },
  navItem: {
    display: "flex", alignItems: "center", padding: "10px 12px",
    borderRadius: 8, fontSize: "0.88rem", color: "#ccc", cursor: "pointer", userSelect: "none",
  },
  navIcon: { marginRight: 10, fontSize: "1rem", width: 20, textAlign: "center" },
  navBadge: {
    background: "rgba(25,253,145,0.13)", color: "#19fd91",
    fontSize: "0.7rem", fontWeight: 700, padding: "2px 8px", borderRadius: 20,
  },
  sidebarFooter: { marginTop: "auto", paddingTop: 14, borderTop: "1px solid #1c1c1c" },
  main: { flex: 1, padding: "28px 30px", overflowX: "auto" },
  statsRow: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 22 },
  statCard: {
    background: "#141414", border: "1px solid #1e1e1e", borderRadius: 14,
    padding: "20px 20px", display: "flex", alignItems: "center", gap: 14,
  },
  statIconWrap: {
    width: 44, height: 44, background: "rgba(255,255,255,0.04)", borderRadius: 10,
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.4rem", flexShrink: 0,
  },
  statValue: { fontSize: "1.9rem", fontWeight: 800, color: "#19fd91", lineHeight: 1 },
  statLabel:  { fontSize: "0.77rem", color: "#666", marginTop: 4 },
  card: {
    background: "#141414", border: "1px solid #1e1e1e",
    borderRadius: 14, padding: "18px 22px", marginBottom: 18,
  },
  cardHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    marginBottom: 16, flexWrap: "wrap", gap: 10,
  },
  cardTitle: { display: "flex", alignItems: "center", fontSize: "0.98rem", fontWeight: 700, color: "#fff" },
  searchInput: {
    background: "#1a1a1a", border: "1px solid #272727", borderRadius: 8,
    padding: "7px 13px", fontSize: "0.83rem", color: "#ddd", outline: "none", width: 210,
  },
  table: { width: "100%", borderCollapse: "collapse" },
  truncate: {
    display: "block", overflow: "hidden", textOverflow: "ellipsis",
    whiteSpace: "nowrap", maxWidth: "100%",
  },
  th: {
    textAlign: "left", fontSize: "0.7rem", fontWeight: 600, color: "#4a4a4a",
    textTransform: "uppercase", letterSpacing: "0.07em", padding: "7px 14px", borderBottom: "1px solid #1e1e1e",
  },
  tr:  { borderBottom: "1px solid #1a1a1a" },
  td:  { padding: "13px 14px", fontSize: "0.87rem", color: "#d0d0d0", verticalAlign: "middle" },
  nameCell: { display: "flex", alignItems: "center", gap: 10 },
  avatarCircle: {
    width: 32, height: 32, borderRadius: "50%", display: "inline-flex",
    alignItems: "center", justifyContent: "center",
    fontSize: "0.82rem", fontWeight: 700, color: "#fff", flexShrink: 0,
  },
  viewBtn: {
    background: "rgba(25,253,145,0.12)", border: "1px solid rgba(25,253,145,0.28)",
    color: "#19fd91", borderRadius: 7, padding: "6px 16px",
    fontSize: "0.8rem", cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap",
  },
  deleteBtn: {
    background: "rgba(255,80,80,0.10)",
    border: "1px solid rgba(255,80,80,0.28)",
    color: "#ff6b6b",
    borderRadius: 7,
    padding: "6px 16px",
    fontSize: "0.8rem",
    cursor: "pointer",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  backBtn: {
    background: "rgba(255,255,255,0.05)", border: "1px solid #282828",
    color: "#999", borderRadius: 7, padding: "6px 14px",
    fontSize: "0.8rem", cursor: "pointer", fontWeight: 500,
  },
  metaRow:   { display: "flex", gap: 28, flexWrap: "wrap" },
  metaItem:  { display: "flex", flexDirection: "column", gap: 3 },
  metaLabel: {
    fontSize: "0.68rem", color: "#4a4a4a", textTransform: "uppercase",
    letterSpacing: "0.06em", fontWeight: 600,
  },
  metaValue: { fontSize: "0.88rem", color: "#ccc", fontWeight: 500 },
  detailLabel: {
    fontSize: "0.7rem", color: "#4a4a4a", textTransform: "uppercase",
    letterSpacing: "0.06em", fontWeight: 600, marginBottom: 10,
  },
  detailBox: {
    background: "#1a1a1a", border: "1px solid #222",
    borderRadius: 10, padding: "16px 18px",
  },
};

export default AdminDashboard;